import { Controller } from "stimulus";

// Click-to-edit for a single field, saved over the same JSON PATCH endpoint the auto-save forms
// post to (`{ ok: true }`). Built for the live console, where sending a creator to a settings page
// mid-broadcast to fix a typo in the title is the wrong shape.
//
// Progressive enhancement: the value is server-rendered as plain text, so without JS the field
// still reads correctly - it just cannot be edited here.
//
// Deliberately not the auto-save controller. That one debounces ten seconds and saves on blur,
// which is right for a settings form and wrong for a field with an explicit Save button sitting
// next to live chat.
export default class extends Controller {
  static targets = ["display", "form", "input", "output", "blank", "error"];

  static values = { url: String, mirror: String };

  connect() {
    this.original = this.hasInputTarget ? this.inputTarget.value : "";
  }

  edit(event) {
    if (event) event.preventDefault();

    this.original = this.inputTarget.value;
    this.hideError();
    this.displayTarget.hidden = true;
    this.formTarget.hidden = false;
    this.inputTarget.focus();

    // Caret to the end rather than selecting all - this is nearly always a tweak, not a rewrite.
    const end = this.inputTarget.value.length;
    if (this.inputTarget.setSelectionRange) this.inputTarget.setSelectionRange(end, end);
  }

  cancel(event) {
    if (event) event.preventDefault();

    this.inputTarget.value = this.original;
    this.close();
  }

  // Escape always cancels. Enter saves a single-line field but is left alone in a textarea, where
  // it has to keep meaning "newline".
  onKeydown(event) {
    if (event.key === "Escape") {
      this.cancel(event);
      return;
    }

    if (event.key === "Enter" && this.inputTarget.tagName !== "TEXTAREA") {
      event.preventDefault();
      this.save(event);
    }
  }

  async save(event) {
    if (event) event.preventDefault();
    if (this.saving) return;

    const value = this.inputTarget.value;
    this.saving = true;

    let ok = false;
    try {
      ok = await this.submit(value);
    } finally {
      this.saving = false;
    }

    // Stay open on failure with the typed value intact, so a save that didn't land can be retried
    // rather than silently discarded behind a closed editor.
    if (!ok) {
      this.showError();
      return;
    }

    this.original = value;
    this.render(value);
    this.mirror(value);
    this.close();
  }

  // Other places on the page showing the same value - the breadcrumb leaf on the live console,
  // which would otherwise disagree with the heading just edited until the next page load. Cleared
  // to blank, they take the empty state's text, so all three never disagree.
  //
  // Deliberately not <title>: that element carries an app-name prefix this would strip, and a bare
  // `title` selector also matches every inline SVG <title>, silently overwriting icon labels.
  mirror(value) {
    if (!this.mirrorValue) return;

    const text = value.trim() === "" && this.hasBlankTarget ? this.blankTarget.textContent.trim() : value;
    if (text === "") return;

    document.querySelectorAll(this.mirrorValue).forEach((el) => {
      el.textContent = text;
    });
  }

  submit(value) {
    const body = new FormData();
    const token = document.getElementsByName("csrf-token")[0];
    if (token) body.append("authenticity_token", token.content);
    body.append(this.inputTarget.name, value);

    return fetch(this.urlValue, {
      method: "PATCH",
      headers: { Accept: "application/json" },
      body: body,
    })
      .then((res) => res.json())
      .then((data) => data.ok === true)
      .catch(() => false);
  }

  // Both variants are server-rendered, so no Tailwind class is ever assembled here - the swap
  // rides on the `hidden` attribute, which Tailwind's preflight emits as `display: none !important`.
  render(value) {
    const blank = value.trim() === "";

    if (this.hasOutputTarget) {
      this.outputTarget.textContent = value;
      this.outputTarget.hidden = blank;
    }

    if (this.hasBlankTarget) this.blankTarget.hidden = !blank;
  }

  close() {
    this.formTarget.hidden = true;
    this.displayTarget.hidden = false;
    this.hideError();
  }

  showError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = false;
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
  }
}
